import { useState } from "react"

import { toast } from "sonner"

import type { TFunction } from "i18next"

import { Check, Copy } from "@/components/icons"
import { Button } from "@/components/ui/button"

const T = "adminDashboard.whatsNew"

const SKELETON_JSON = `{
  "version": null
}`

/**
 * "How it works" popover body. Two sections — naming + resolution — kept
 * tight so the popover doesn't dominate the dashboard while still teaching
 * an operator the rules they're most likely to fumble (locale shape, custom
 * version suffixes, the fall-through resolver chain).
 */
export function HowItWorksPopoverBody({ t }: { t: TFunction }) {
  return (
    <div className="space-y-4 text-xs">
      {/* Section 1 — File naming. The two segments operators are most likely
          to fumble are locale shape (BCP 47-without-script) and the suffix
          rules for custom versions (PEP 440 local-version segment). */}
      <section>
        <h3 className="text-sm font-semibold">{t(`${T}.helpNamingTitle`, "1. File naming")}</h3>
        <p className="text-muted-foreground mt-1 leading-relaxed">
          {t(
            `${T}.helpNamingIntro`,
            "Each release note is a markdown file at the root of the blob container, named:"
          )}
        </p>
        <code className="bg-muted/50 mt-1.5 block rounded p-1.5 font-mono">
          whats-new-{"{locale}"}-{"{version}"}.md
        </code>
        <ul className="text-muted-foreground mt-2 list-disc space-y-1.5 pl-4 leading-relaxed">
          <li>
            <strong className="text-foreground">{t(`${T}.helpNamingLocale`, "Locale")}:</strong>{" "}
            {t(
              `${T}.helpNamingLocaleBody`,
              "2–3 letter language tag (en, sv, fil), optionally followed by a 2-letter region (en-US, pt-BR) or 3-digit UN region code (es-419). Casing is normalised on the server (language lowercase, region uppercase) — clients can send en-us. Script subtags like zh-Hans aren't supported because they collide with notice prefixes."
            )}
          </li>
          <li>
            <strong className="text-foreground">{t(`${T}.helpNamingVersion`, "Version")}:</strong>{" "}
            {t(
              `${T}.helpNamingVersionBody`,
              "starts with an alphanumeric and may contain dots, underscores, plus, hyphens. Standard PEP 440 forms work — including pre-releases (4.78.1rc1) and local-version suffixes for customer- or context-specific notes (4.78.1+customer-x, 4.78.1+hotfix). Suffixes after + are ignored by version comparisons but keep the file uniquely named, so you can ship a customer-specific override without colliding with the canonical release."
            )}
          </li>
          <li>
            <strong className="text-foreground">
              {t(`${T}.helpNamingNotice`, "Notice files")}:
            </strong>{" "}
            {t(
              `${T}.helpNamingNoticeBody`,
              "any version starting with letters (aoai-incident, gdpr-update) is treated as a notice. Notices are listed in the table and pinnable, but excluded from auto-resolve because they don't parse as PEP 440."
            )}
          </li>
        </ul>
      </section>

      {/* Section 2 — Resolution chain. Mirror the order documented in the
          backend resolver and admin endpoint, with the fall-through rule
          spelled out so an operator with a broken pin / override doesn't
          panic when users keep seeing release notes anyway. */}
      <section>
        <h3 className="text-sm font-semibold">
          {t(`${T}.helpResolutionTitle`, "2. Resolution order (per request)")}
        </h3>
        <ol className="text-muted-foreground mt-1 list-decimal space-y-1.5 pl-4 leading-relaxed">
          <li>
            <strong className="text-foreground">
              {t(`${T}.helpResolutionPin`, "Customer pin")}
            </strong>{" "}
            {t(`${T}.helpResolutionPinBody`, "(Cosmos) — set from this dashboard, per deployment.")}
          </li>
          <li>
            <strong className="text-foreground">
              {t(`${T}.helpResolutionOverride`, "Global override")}
            </strong>{" "}
            {t(
              `${T}.helpResolutionOverrideBody`,
              "(whats-new-override.json in the blob) — broadcasts a version to every deployment that doesn't have its own pin."
            )}
          </li>
          <li>
            <strong className="text-foreground">
              {t(`${T}.helpResolutionAuto`, "Auto-applicable")}
            </strong>{" "}
            {t(
              `${T}.helpResolutionAutoBody`,
              "— the highest PEP 440 version in the container that is ≤ this deployment's app version. Future-staged blobs (newer than the deploy) are skipped until the deploy catches up."
            )}
          </li>
        </ol>
        <p className="text-muted-foreground mt-2 leading-relaxed">
          {t(
            `${T}.helpResolutionFallthrough`,
            "Each step falls through to the next if its blob is missing — a stale pin or override never blanks the dialog out. For the picked version, locale fallback tries the full canonical locale (en-US), then the base language (en), then DEFAULT_LOCALE."
          )}
        </p>
      </section>

      <p className="text-muted-foreground">
        {t(`${T}.helpFooter`, "Full workflow: docs/operations.md.")}
      </p>
    </div>
  )
}

/**
 * Override-file skeleton popover body. Renders the canonical filename, a
 * one-line activation hint, and the JSON snippet with a copy-to-clipboard
 * button (the file is meant to be copy-pasted into the blob container during
 * an outage; clicking past the popover to a code block shouldn't require a
 * manual selection).
 */
export function SkeletonPopoverBody({ filename, t }: { filename: string; t: TFunction }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SKELETON_JSON)
      setCopied(true)
      // Reset the copied indicator without leaving the user wondering whether
      // it stuck — long enough to register, short enough they can re-copy.
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error(t(`${T}.copyFailed`, "Couldn't copy to clipboard"))
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-semibold">
          {t(`${T}.skeletonTitle`, "Override file skeleton")}
        </div>
        <p className="text-muted-foreground mt-1 text-xs">
          {t(
            `${T}.skeletonHint`,
            "Upload as {{filename}} at the root of the blob container. Activate by changing version to a string that exists in the table above; deactivate by setting it back to null.",
            { filename }
          )}
        </p>
      </div>
      <div className="relative">
        <pre className="bg-muted/50 overflow-x-auto rounded p-2 pr-10 text-xs">{SKELETON_JSON}</pre>
        <Button
          aria-label={t(`${T}.copy`, "Copy to clipboard")}
          className="absolute top-1 right-1 size-7"
          size="sm"
          variant="ghost"
          onClick={handleCopy}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </Button>
      </div>
    </div>
  )
}
